import { AdminPageHeader } from "@admin-ui/components/composed/AdminPageHeader";
import { AdminLayout } from "@admin-ui/components/frames/AdminLayout";
import { Head, useForm } from "@inertiajs/react";
import { adminDestinationsMapboxSearchesPath, adminDestinationsPath } from "@travily/routes";
import { type ReactNode, useCallback, useMemo } from "react";
import type { MapboxSearchResult } from "@/components/admin/MapboxSearch";
import { useAdminLayoutAccount } from "@/hooks/admin/use-admin-layout-account";
import AdminPersistentLayout from "@/layouts/admin-persistent-layout";
import { useMap } from "@/layouts/map-host";
import {
  ADMIN_MAP_BOUNDS_PADDING,
  ADMIN_MAP_CLASSNAME,
  type BboxCoords,
  BboxRectangle,
  bboxToEnvelope,
} from "./components/bbox-rectangle";
import { NewView } from "./new.view";

interface FromLocation {
  id: number;
  name: string;
  country_code: string;
  mapbox_id: string;
  latitude: number;
  longitude: number;
}

interface AdminDestinationsNewPageProps {
  from_location?: FromLocation;
  available_countries: Array<{ iso_alpha2: string; name: string }>;
}

const DEFAULT_BBOX_OFFSET = 0.1;

function AdminDestinationsNewPage({ from_location, available_countries }: AdminDestinationsNewPageProps) {
  const account = useAdminLayoutAccount();
  const form = useForm({
    name: from_location?.name ?? "",
    country_code: from_location?.country_code ?? "",
    description: "",
    mapbox_id: from_location?.mapbox_id ?? "",
    latitude: from_location?.latitude ?? 0,
    longitude: from_location?.longitude ?? 0,
    bbox_north: from_location ? from_location.latitude + DEFAULT_BBOX_OFFSET : 0,
    bbox_south: from_location ? from_location.latitude - DEFAULT_BBOX_OFFSET : 0,
    bbox_east: from_location ? from_location.longitude + DEFAULT_BBOX_OFFSET : 0,
    bbox_west: from_location ? from_location.longitude - DEFAULT_BBOX_OFFSET : 0,
    photo_signed_id: "",
    photo_stock_url: "",
  });

  const { data, setData, post, processing, transform } = form;
  transform((formData) => ({ destination: formData }));
  const hasSelection = data.mapbox_id !== "";
  const hasBbox = data.bbox_north !== 0 || data.bbox_south !== 0;

  const bbox = useMemo<BboxCoords>(
    () => ({ north: data.bbox_north, south: data.bbox_south, east: data.bbox_east, west: data.bbox_west }),
    [data.bbox_north, data.bbox_south, data.bbox_east, data.bbox_west],
  );
  const envelope = useMemo(() => (hasBbox ? bboxToEnvelope(bbox) : undefined), [hasBbox, bbox]);

  const handleBboxChange = useCallback(
    (next: BboxCoords) => {
      setData((prev) => ({
        ...prev,
        bbox_north: next.north,
        bbox_south: next.south,
        bbox_east: next.east,
        bbox_west: next.west,
      }));
    },
    [setData],
  );

  const overlay = useMemo(
    () => (hasBbox ? <BboxRectangle bbox={bbox} editable={hasSelection} onBboxChange={handleBboxChange} /> : null),
    [hasBbox, bbox, hasSelection, handleBboxChange],
  );

  const mapSlotRef = useMap({
    overlay,
    className: ADMIN_MAP_CLASSNAME,
    fitBounds: envelope,
    fitBoundsPadding: ADMIN_MAP_BOUNDS_PADDING,
  });

  const handleMapboxSelect = (result: MapboxSearchResult) => {
    setData({
      ...data,
      name: result.name,
      country_code: result.country_code,
      mapbox_id: result.mapbox_id,
      latitude: result.latitude,
      longitude: result.longitude,
      bbox_north: result.bbox_north ?? result.latitude + DEFAULT_BBOX_OFFSET,
      bbox_south: result.bbox_south ?? result.latitude - DEFAULT_BBOX_OFFSET,
      bbox_east: result.bbox_east ?? result.longitude + DEFAULT_BBOX_OFFSET,
      bbox_west: result.bbox_west ?? result.longitude - DEFAULT_BBOX_OFFSET,
    });
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!hasSelection) return;
    post(adminDestinationsPath());
  };

  const handlePhotoChange = useCallback(
    (signedId: string, stockPhotoUrl: string) =>
      setData((prev) => ({ ...prev, photo_signed_id: signedId, photo_stock_url: stockPhotoUrl })),
    [setData],
  );

  return (
    <AdminLayout activeUrl={adminDestinationsPath()} account={account}>
      <Head title="New destination - Admin" />

      <AdminPageHeader
        breadcrumbs={[{ label: "Destinations", href: adminDestinationsPath() }, { label: "New" }]}
        title="New destination"
      />

      <div className="mx-auto w-full max-w-container px-4 lg:px-8">
        <NewView
          name={data.name}
          countryCode={data.country_code}
          description={data.description}
          availableCountries={available_countries}
          fromLocationName={from_location?.name}
          hasSelection={hasSelection}
          processing={processing}
          cancelHref={adminDestinationsPath()}
          mapboxSearchPath={adminDestinationsMapboxSearchesPath()}
          map={<div ref={mapSlotRef} />}
          onMapboxSelect={handleMapboxSelect}
          onNameChange={(value) => setData("name", value)}
          onCountryChange={(value) => setData("country_code", value)}
          onDescriptionChange={(value) => setData("description", value)}
          onPhotoChange={handlePhotoChange}
          onSubmit={handleSubmit}
        />
      </div>
    </AdminLayout>
  );
}

AdminDestinationsNewPage.layout = (page: ReactNode) => <AdminPersistentLayout>{page}</AdminPersistentLayout>;

export default AdminDestinationsNewPage;
